import { useEffect, useRef, useState } from 'react'
import { SpeakerWaveIcon, SpeakerXMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'

/**
 * AmbientSound — suono del mare, opzionale e discreto.
 *
 * Genera la risacca in tempo reale con la Web Audio API (rumore rosa filtrato + una
 * modulazione lenta che imita le onde che vanno e vengono). Nessun file .mp3 da scaricare.
 *
 * Regole di rispetto verso l'utente (coerenti con l'obiettivo di coinvolgere senza infastidire):
 *   - OFF di default: nessun autoplay. Parte solo se l'utente clicca il pulsante.
 *   - La preferenza viene ricordata (localStorage). Se l'utente l'ha già attivato in passato,
 *     al primo gesto sulla pagina il suono riprende (i browser vietano l'audio senza interazione).
 *   - Volume basso, fade-in/out morbido, si silenzia quando la tab non è visibile.
 *
 * Il pulsante è piccolo e fisso in un angolo; non ruba attenzione al contenuto.
 */

const PREF_KEY = 'edive-ambient-sound'

export default function AmbientSound({ className = '' }: { className?: string }) {
  const { t } = useTranslation()
  const [on, setOn] = useState(false)
  const [ready, setReady] = useState(false)

  const ctxRef = useRef<AudioContext | null>(null)
  const gainRef = useRef<GainNode | null>(null)
  const nodesRef = useRef<AudioNode[]>([])
  const wantsOnRef = useRef(false)

  // Costruisce la catena audio (una sola volta, al primo avvio).
  function buildGraph() {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioCtx) return null
    const ctx = new AudioCtx()

    // Rumore rosa: buffer di rumore bianco filtrato → base della risacca.
    const bufferSize = 2 * ctx.sampleRate
    const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
    const data = noiseBuffer.getChannelData(0)
    let b0 = 0, b1 = 0, b2 = 0
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1
      // filtro pink (Paul Kellet, versione ridotta)
      b0 = 0.99765 * b0 + white * 0.099046
      b1 = 0.963 * b1 + white * 0.2965164
      b2 = 0.57 * b2 + white * 1.0526913
      data[i] = (b0 + b1 + b2 + white * 0.1848) * 0.09
    }
    const noise = ctx.createBufferSource()
    noise.buffer = noiseBuffer
    noise.loop = true

    // Passa-basso: toglie il sibilo, lascia il "fruscio d'acqua".
    const lp = ctx.createBiquadFilter()
    lp.type = 'lowpass'
    lp.frequency.value = 650
    lp.Q.value = 0.6

    // LFO lento: modula il volume → l'onda che sale e scende (~ ogni 9 secondi).
    const swellGain = ctx.createGain()
    swellGain.gain.value = 0.5
    const lfo = ctx.createOscillator()
    lfo.type = 'sine'
    lfo.frequency.value = 0.11
    const lfoDepth = ctx.createGain()
    lfoDepth.gain.value = 0.35
    lfo.connect(lfoDepth)
    lfoDepth.connect(swellGain.gain)

    // Gain master con fade.
    const master = ctx.createGain()
    master.gain.value = 0

    noise.connect(lp)
    lp.connect(swellGain)
    swellGain.connect(master)
    master.connect(ctx.destination)

    noise.start()
    lfo.start()

    ctxRef.current = ctx
    gainRef.current = master
    nodesRef.current = [noise, lp, swellGain, lfo, lfoDepth]
    return ctx
  }

  function fadeTo(target: number, seconds = 1.2) {
    const ctx = ctxRef.current
    const master = gainRef.current
    if (!ctx || !master) return
    const now = ctx.currentTime
    master.gain.cancelScheduledValues(now)
    master.gain.setValueAtTime(master.gain.value, now)
    master.gain.linearRampToValueAtTime(target, now + seconds)
  }

  async function start() {
    if (!ctxRef.current) buildGraph()
    const ctx = ctxRef.current
    if (!ctx) return
    if (ctx.state === 'suspended') await ctx.resume()
    fadeTo(0.22)
    setOn(true)
    wantsOnRef.current = true
    localStorage.setItem(PREF_KEY, 'on')
  }

  function stop() {
    fadeTo(0, 0.8)
    setOn(false)
    wantsOnRef.current = false
    localStorage.setItem(PREF_KEY, 'off')
  }

  function toggle() {
    if (on) stop()
    else start()
  }

  // Se l'utente aveva già attivato il suono in una visita precedente, lo riprendiamo
  // al primo gesto (i browser bloccano l'audio senza interazione utente).
  useEffect(() => {
    setReady(true)
    if (localStorage.getItem(PREF_KEY) !== 'on') return
    const resume = () => {
      start()
      window.removeEventListener('pointerdown', resume)
      window.removeEventListener('keydown', resume)
    }
    window.addEventListener('pointerdown', resume, { once: true })
    window.addEventListener('keydown', resume, { once: true })
    return () => {
      window.removeEventListener('pointerdown', resume)
      window.removeEventListener('keydown', resume)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Silenzia quando la tab è nascosta; ripristina se l'utente vuole il suono.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) fadeTo(0, 0.5)
      else if (wantsOnRef.current) fadeTo(0.22, 1)
    }
    document.addEventListener('visibilitychange', onVis)
    return () => document.removeEventListener('visibilitychange', onVis)
  }, [])

  // Pulizia alla dismount.
  useEffect(() => {
    return () => {
      try {
        nodesRef.current.forEach((n) => (n as any).stop?.())
        ctxRef.current?.close()
      } catch {
        /* no-op */
      }
    }
  }, [])

  if (!ready) return null

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={on}
      title={on ? t('atmosphere.soundOff') : t('atmosphere.soundOn')}
      aria-label={on ? t('atmosphere.soundOff') : t('atmosphere.soundOn')}
      className={`group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white/80 backdrop-blur-sm transition-all hover:bg-white/20 hover:text-white ${className}`}
    >
      {on ? (
        <SpeakerWaveIcon className="h-4 w-4 text-teal-300" />
      ) : (
        <SpeakerXMarkIcon className="h-4 w-4" />
      )}
      <span className="hidden sm:inline">
        {on ? t('atmosphere.soundOnLabel') : t('atmosphere.soundOffLabel')}
      </span>
      {on && (
        <span className="flex items-end gap-0.5" aria-hidden="true">
          <span className="w-0.5 rounded bg-teal-300" style={{ height: 8, animation: 'edive-eq 1.1s ease-in-out infinite' }} />
          <span className="w-0.5 rounded bg-teal-300" style={{ height: 12, animation: 'edive-eq 1.1s ease-in-out 0.15s infinite' }} />
          <span className="w-0.5 rounded bg-teal-300" style={{ height: 6, animation: 'edive-eq 1.1s ease-in-out 0.3s infinite' }} />
        </span>
      )}
      <style>{`
        @keyframes edive-eq {
          0%, 100% { transform: scaleY(0.4); }
          50%      { transform: scaleY(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes edive-eq { 0%, 100% { transform: scaleY(0.8); } }
        }
      `}</style>
    </button>
  )
}
